import axios from 'axios'
import { isEmpty } from 'lodash'
import { MASTER_DATA_FIELDS } from '~/common/constants'
import { action, computed, observable, persist, runInAction, store } from '~/common/mobx.decorator'
import { apolloClient, GET_MASTER_DATA, GET_SPECIALTY_MASTER_QUERY, LIKE_DISLIKE_JOB_MUTATION } from '~/common/apollo'
import { authStore, notifyStore } from '~/stores'
import { redirectEventTrackingBookMark } from '~/common/helpers/bookMark.helper'
import { mappingObjectToArrayField } from '~/common/helpers/mapping.helper'
import { careStore } from '~/companies/care/care.store'
import { parsePathname } from '~/common/helpers/url.helper'

const MIN_PATH_JOBS_DETAIL = 2
const MAX_PATH_JOBS_DETAIL = 8

@store()
class MasterStore {
	@observable disciplines
	@observable disciplinesData
	@observable referrals
	@observable agencies
	@observable chartings
	@observable primarySpecialities
	@observable secondarySpecialities
	@persist @observable licenseStates
	@observable referralRelationships
	@observable jobDisciplines
	@observable jobShifts
	@observable originalSpecialties = {}
	@observable specialties = []
	@observable onlySpecialtyOptions = []
	@observable reasons = []
	@observable locations = []
	@observable salutations = []
	@observable relationships = []
	@observable timezoneIdentifiers = []
	@observable documentTypes = []
	@observable facilityTypes = []
	@observable facilityTypesSettings = []
	@observable referredRelationships = []
	@observable workLocations = []
	@observable originalLocations = []
	@observable specialtyMasters = []
	@observable existingSpecialty = false
	@observable assignmentTypes = []
	@observable cityStates = []
	@observable isJobDetailPage = false
	@observable jobApplicantStatus = []
	@observable savedFilterFrequencies = []
	@observable credentialVisibleFields = {}

	@persist @observable businessSectorNames = []
	@persist @observable specialtyOptions = []
	@persist @observable listSpecialtyName = []
	@persist @observable jobTypes
	@persist @observable masterData = {}
	@persist @observable frequencyMasterData = {}
	@persist @observable alertChannelMasterData = {}
	@persist @observable pauseAlertPeriodMasterData = {}
	@persist @observable workerPostHireContent = {}
	@persist @observable candidatePostHireContent = {}
	@persist @observable degreeLevels = []

	@computed
	get licenseSpecialtyOptions() {
		const specialties = []
		if (this.disciplinesData) {
			this.disciplinesData.forEach((discipline) => {
				if (discipline.specialties && discipline.specialties.length) {
					discipline.specialties.forEach((specialty) => {
						specialties.push({
							value: specialty?.id || specialty?.name,
							label: specialty?.name,
							parentValue: discipline.value,
						})
					})
				}
			})
		}
		return specialties
	}

	@action
	fetchDisciplines = async (force = false) => {
		if (!force && !isEmpty(this.disciplinesData)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.discipline,
			},
		})
		this.disciplinesData = response.data.map((item) => ({
			label: item.name,
			value: item.id,
			specialties: item.specialties,
		}))
	}

	@action
	fetchCandidateTypes = async () => {
		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.candidateType,
			},
		})
		runInAction(() => {
			this.businessSectorNames = response.data.map((item) => ({
				label: item.name,
				value: item.id,
			}))
		})
	}

	@action
	fetchTypeJobs = async (force = false) => {
		if (!force && !isEmpty(this.disciplines)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.jobType,
			},
		})
		this.jobTypes = response.data.map((item) => ({
			label: item.name,
			value: item.name,
		}))
	}
	@action
	fetchTimezoneIdentifiers = async () => {
		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.timezoneIdentifiers,
			},
		})
		this.timezoneIdentifiers = response.data.map((item) => ({
			label: item.abbreviation,
			value: item.name,
		}))
	}

	@action
	fetchFacilityTypesSettings = async (force = false) => {
		if (!force && !isEmpty(this.disciplines)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.facilityTypes,
			},
		})
		this.facilityTypesSettings = response.data.map((item) => ({
			label: item?.name,
			value: item?.name,
		}))
	}

	@action
	fetchJobDisciplines = async (force = false) => {
		if (!force && !isEmpty(this.jobDisciplines)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: 'discipline',
			},
		})

		let listSpecialtyName = []
		let onlySpecialtyOptions = []
		const disciplines = response.data.map((item) => {
			listSpecialtyName = [...listSpecialtyName, ...item.specialties.map((specialty) => ({ label: specialty.long_name, value: specialty.long_name }))]
			onlySpecialtyOptions = [...onlySpecialtyOptions, ...item.specialties.map((specialty) => ({ label: specialty.name, value: specialty.id }))]
			return {
				label: item.name,
				value: item.id,
				child: item.specialties.map((specialty) => ({ label: specialty.name, value: specialty.id, parent: item.id })),
			}
		})

		this.onlySpecialtyOptions = onlySpecialtyOptions

		this.disciplines = disciplines

		this.specialtyOptions = disciplines

		this.listSpecialtyName = listSpecialtyName

		this.jobDisciplines = response.data
	}

	@action
	fetchJobShifts = async (force = false) => {
		if (!force && !isEmpty(this.jobShifts)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: 'shift',
			},
		})
		this.jobShifts = response.data?.map((item) => ({ label: item.name, value: item.name }))
	}

	@action
	fetchLocations = async (force = false) => {
		if (!force && !isEmpty(this.locations)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.preferredLocations,
			},
		})

		this.locations = response.data
	}

	@action
	fetchReferralRelationships = async (force = false) => {
		if (!force && !isEmpty(this.referralRelationships)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.referralRelationship,
			},
		})

		this.referralRelationships = response.data
	}

	@action
	fetchTravelerRelationship = async (force = false) => {
		if (!force && !isEmpty(this.referredRelationships)) {
			return
		}
		const response = await axios.get(`/api/public/v1/master_data`)

		const relationships = response?.data?.referred_traveler?.relationship
		let data = Object.keys(relationships).map((key) => ({
			label: relationships[key],
			value: key,
		}))

		this.referredRelationships = data
	}

	@action
	fetchReferrals = async (force = false) => {
		if (!force && !isEmpty(this.referrals)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.referral,
			},
		})

		this.referrals = response.data
	}

	@action
	fetchAgencies = async (force = false) => {
		if (!force && !isEmpty(this.agencies)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.agency,
			},
		})

		this.agencies = response.data
	}

	@action
	fetchChartings = async (force = false) => {
		if (!force && !isEmpty(this.chartings)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.charting,
			},
		})

		this.chartings = response.data
	}

	@action
	fetchSecondarySpecialities = async (force = false) => {
		if (!force && !isEmpty(this.secondarySpecialities)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.secondarySpecialty,
			},
		})

		this.secondarySpecialities = response.data
	}

	@action
	fetchWorkerPostHireContent = async (force = false) => {
		if (!force && !isEmpty(this.workerPostHireContent)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.workerPostHireContent,
			},
		})
		this.workerPostHireContent = response.data
	}

	@action
	fetchCandidatePostHireContent = async (force = false) => {
		if (!force && !isEmpty(this.candidatePostHireContent)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.candidatePostHireContent,
			},
		})
		this.candidatePostHireContent = response.data
	}

	@action
	fetchLicenseStates = async (force = false) => {
		if (!force && !isEmpty(this.licenseStates)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.licenseState,
			},
		})
		this.licenseStates = response.data.map((item) => ({ label: item?.name, value: item?.state_code }))
	}

	@action
	fetchPrimarySpecialities = async (force = false) => {
		if (!force && !isEmpty(this.primarySpecialities)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.primarySpecialty,
			},
		})

		this.primarySpecialities = response.data
	}

	@action
	onFavoriteJob = async (values) => {
		const { jobId, action } = values?.variables
		try {
			const response = await apolloClient.mutate({
				mutation: LIKE_DISLIKE_JOB_MUTATION,
				variables: {
					jobId: jobId,
					action: action,
				},
			})
			notifyStore.success('$MESSAGES.SUCCESSFUL')
			return response?.data?.likeOrDislikeAJob
		} catch (error) {
			notifyStore.error(error.message)
		}
	}

	@action
	onTrackingFavorite = async (jobId, flag, finalizeData) => {
		if (finalizeData?.type) {
			if (finalizeData?.status === 'EVENT_ORDER_CITY') {
				await redirectEventTrackingBookMark(finalizeData.type, {
					job_id: jobId,
					is_bookmarked: !flag,
					worker_email: authStore.workerEmail,
					order_number: finalizeData.order,
					city_id: finalizeData.cityId,
				})
			} else if (finalizeData?.status === 'EVENT_ORDER') {
				await redirectEventTrackingBookMark(finalizeData.type, {
					job_id: jobId,
					is_bookmarked: !flag,
					worker_email: authStore.workerEmail,
					order_number: finalizeData.order,
				})
			} else {
				await redirectEventTrackingBookMark(finalizeData.type, {
					job_id: jobId,
					is_bookmarked: !flag,
					worker_email: authStore.workerEmail,
				})
			}
		}
	}

	@action
	fetchRelationships = async (force = false) => {
		if (!force && !isEmpty(this.relationships)) {
			return
		}

		const response = await axios.get(`/api/public/v1/master_data`)
		this.relationships = response?.data?.worker_reference?.relationship
	}

	@action
	fetchSalutations = async (force = false) => {
		if (!force && !isEmpty(this.salutations)) {
			return
		}

		const response = await axios.get(`/api/public/v1/master_data`)
		this.salutations = response?.data?.worker_reference?.salutation
	}

	@action
	fetchReasonLeavings = async (force = false) => {
		if (!force && !isEmpty(this.reasons)) {
			return
		}
		const response = await axios.get(`/api/public/v1/master_data`)
		this.reasons = response?.data?.work_experience?.reason_for_leaving
	}

	@action
	fetchAssignmentTypes = async (force = false) => {
		if (!force && !isEmpty(this.assignmentTypes)) {
			return
		}
		const response = await axios.get(`/api/public/v1/master_data`)
		this.assignmentTypes = response?.data?.work_experience?.assignment_type
	}

	/// This function is wrong, it should be removed
	@action
	fetchSpecialties = async () => {
		// if (!force && !isEmpty(this.specialties)) {
		// 	return
		// }
		// const response = await axios.get(`/api/public/v1/picklists`, {
		// 	params: {
		// 		picklist_type: MASTER_DATA_FIELDS.specialty,
		// 	},
		// })
		// this.originalSpecialties = response.data
		// let data = []
		// Object.values(response.data).map((item, index) => {
		// 	let dataArray = Object.values(item)
		// 	const category = Object.keys(response.data)[index]
		// 	dataArray = dataArray.map((item) => ({ ...item, category: category }))
		// 	data = [...data, ...dataArray]
		// 	return data
		// })
		// this.specialties = data
	}

	@action
	fetchSupportDocumentTypes = async (force = false) => {
		if (!force && !isEmpty(this.documentTypes)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.documentType,
			},
		})

		this.documentTypes = response.data
	}

	@action
	fetchFacilityTypes = async (force = false) => {
		if (!force && !isEmpty(this.facilityTypes)) {
			return
		}
		const response = await axios.get(`/api/public/v1/master_data`)
		this.facilityTypes = response?.data?.work_experience?.facility_type
	}

	@action
	fetchWorkLocations = async (force = false) => {
		if (!force && !isEmpty(this.workLocations)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.state,
			},
		})

		const locations = response?.data

		this.originalLocations = response?.data
		let data = Object.keys(locations).map((key) => ({
			label: locations[key],
			value: key,
		}))

		this.workLocations = data
	}

	@action
	fetchJobApplicantStatus = async (force = false) => {
		if (!force && !isEmpty(this.jobApplicantStatus)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.jobApplicantStatus,
			},
		})

		this.jobApplicantStatus = response?.data
		return this.jobApplicantStatus
	}

	@computed
	get checkIsJobDetailPage() {
		return this.existingSpecialty && careStore.getActiveFeature()?.code === 'jobs'
	}

	@action
	fetchCityState = async (force = false, input) => {
		if (!force && !isEmpty(this.cityStates)) {
			return
		}

		const response = await axios.get(`/cities/search?terms=${input}`)

		const locations = response?.data?.data

		this.originalLocations = response?.data
		let data = Object.keys(locations).map((key) => ({
			label: locations[key],
			value: key,
		}))

		this.cityStates = data
	}
	@action
	fetchSavedFilterData = async () => {
		const response = await apolloClient.query({
			query: GET_MASTER_DATA,
		})

		this.masterData = response.data.appInit.masterData
		this.frequencyMasterData = mappingObjectToArrayField(this.masterData.save_filter.frequency)
		this.alertChannelMasterData = mappingObjectToArrayField(this.masterData.save_filter.alert_channel)
		this.pauseAlertPeriodMasterData = mappingObjectToArrayField(this.masterData.save_filter.pause_alert_period)
	}

	@action
	fetchSavedFilterFrequencies = async (force = false) => {
		if (!force && !isEmpty(this.savedFilterFrequencies)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.savedFilterFrequencies,
			},
		})
		this.savedFilterFrequencies = response.data.map((item) => ({ label: item?.name, value: item?.id }))
	}

	@action
	fetchSavedFilterPauseAlertPeriods = async (force = false) => {
		if (!force && !isEmpty(this.savedFilterPauseAlertPeriods)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.savedFilterPauseAlertPeriods,
			},
		})
		this.savedFilterPauseAlertPeriods = response.data.map((item) => ({ label: item?.name, value: item?.id }))
	}
	@action
	fetchSavedFilterNotificationChannels = async (force = false) => {
		if (!force && !isEmpty(this.savedFilterNotificationChannels)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.savedFilterNotificationChannels,
			},
		})
		this.savedFilterNotificationChannels = response.data.map((item) => ({ label: item?.name, value: item?.id }))
	}

	@action
	fetchCredentialVisibleFields = async (force = false) => {
		if (!force && !isEmpty(this.credentialVisibleFields)) {
			return
		}

		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.credentialVisibleFields,
			},
		})

		this.credentialVisibleFields = response.data
	}

	@action
	fetchSpecialtyMasters = async () => {
		const response = await apolloClient.query({
			query: GET_SPECIALTY_MASTER_QUERY,
		})
		this.specialtyMasters = response.data.specialtiesMaster
	}
	@action
	getLabelCurrentTimeZone = (nameTimezone) => {
		return this.timezoneIdentifiers.find((item) => item.value === nameTimezone)?.label
	}

	@action
	checkExistingSpecialty = (params) => {
		const { id } = params
		const pathName = window.location.pathname
		const convertedPathName = parsePathname(pathName)
		const specialtyArray = this.specialtyMasters
		this.existingSpecialty = !!specialtyArray.find((item) => item.slug === id)
		if ((!this.existingSpecialty && convertedPathName.length === MIN_PATH_JOBS_DETAIL) || convertedPathName.length === MAX_PATH_JOBS_DETAIL) {
			this.setIsJobDetailPage(true)
		} else {
			this.setIsJobDetailPage(false)
		}
		return this.existingSpecialty
	}

	@action
	setIsJobDetailPage = (value) => {
		this.isJobDetailPage = value
	}

	@action
	fetchDegreeLevels = async (force = false) => {
		if (!force && !isEmpty(this.degreeLevels)) {
			return
		}
		const response = await axios.get(`/api/public/v1/picklists`, {
			params: {
				picklist_type: MASTER_DATA_FIELDS.degreeLevel,
			},
		})

		this.degreeLevels = response?.data.map((item) => ({ label: item?.name, value: item?.id }))
	}
}

export const masterStore = new MasterStore()
